import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import {
  mdiAlertCircleOutline,
  mdiCalendarAlertOutline,
  mdiCalendarCheckOutline,
  mdiCalendarClockOutline,
  mdiCalendarRemoveOutline,
  mdiCalendarSyncOutline,
  mdiCheckBold,
  mdiCloseCircleOutline,
  mdiMessageTextOutline,
  mdiProgressClock,
} from "@mdi/js"

type CaseCommunicationProjection = {
  id: string
  created_at: string
  purpose: string
  status: string
  channel?: string | null
  recipient_value?: string | null
  recipient_display_name?: string | null
  subject?: string | null
  queued_at?: string | null
  sent_at?: string | null
  failed_at?: string | null
  provider_status?: string | null
  failure_reason?: string | null
  skipped_reason?: string | null
  suppression_reason?: string | null
}

export type CommunicationTypePresentation = {
  status: StatusDefinition
  tooltip: string
}

export type CommunicationMessagePresentation = CommunicationTypePresentation & {
  resultLabel: string
}

export type CommunicationExecutionPresentation = CommunicationTypePresentation

export type CaseCommunicationTimelinePresentationItem = {
  id: string
  createdAt: string
  title: string
  detail: string
  channel?: string | null
  status: string
  statusClass: string
  statusDefinition: StatusDefinition
  recipient?: string | null
  subject?: string | null
  source: "communication" | "case_communication"
}

const normalizeCode = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toUpperCase()

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")

const tooltipLines = (parts: Array<string | null | undefined>) =>
  parts
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .map(escapeHtml)
    .join("<br>")

const communicationReason = (communication: CaseCommunicationProjection) =>
  communication.failure_reason ||
  communication.skipped_reason ||
  communication.suppression_reason ||
  null

const communicationDeliverySubject = (channel?: string | null) => {
  switch (normalizeCode(channel)) {
    case "EMAIL":
      return "Die E-Mail"
    case "MOBILE_MESSAGE":
      return "Die mobile Nachricht"
    case "LETTER":
      return "Der Brief"
    case "PHONE":
      return "Der Anruf"
    case "CHAT":
      return "Die Chat-Nachricht"
    case "VOICE":
      return "Der Telefonbot-Kontakt"
    default:
      return "Die Nachricht"
  }
}

const reasonResultLabel = (reason: string | null | undefined) => {
  switch (reason) {
    case "no_safe_email_recipient":
      return "Keine eindeutige E-Mail-Adresse"
    case "admin_selected_no_customer_message":
      return "Ohne Nachricht dokumentiert"
    case "reschedule_pending":
      return "Storno durch Verschiebung ersetzt"
    case "missing_work_order":
      return "Auftrag nicht gefunden"
    case "missing_public_action_code":
      return "Kontaktcode fehlt"
    default:
      return reason ? "Nicht gesendet" : null
  }
}

const sentResultLabelForPurpose = (purpose: string) => {
  switch (purpose) {
    case "APPOINTMENT_CONFIRMATION":
      return "Bestätigung versendet"
    case "APPOINTMENT_RESCHEDULE":
      return "Verschiebung versendet"
    case "APPOINTMENT_CANCELLATION":
      return "Absage versendet"
    case "APPOINTMENT_CANCEL_REPLAN_LATER":
      return "Ausfallinfo versendet"
    default:
      return "Nachricht versendet"
  }
}

export const formatCommunicationDateTime = (value?: string | null) => {
  if (!value) return ""

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return ""

  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

export const communicationHeadline = (
  item: Pick<CaseCommunicationTimelinePresentationItem, "title" | "createdAt">,
) => {
  const timestamp = formatCommunicationDateTime(item.createdAt)

  return timestamp ? `${item.title} vom ${timestamp}` : item.title
}

export const communicationPurposePresentation = (
  purpose: string,
): CommunicationTypePresentation => {
  switch (purpose) {
    case "APPOINTMENT_CONFIRMATION":
      return {
        status: {
          value: purpose,
          label: "Terminbestätigung",
          icon: mdiCalendarCheckOutline,
          color: "emerald",
        },
        tooltip:
          "Bestätigung eines vereinbarten Termins. Enthält Terminzeit, Auftrag und die für den Kunden relevanten Kontaktdaten.",
      }
    case "APPOINTMENT_CANCELLATION":
      return {
        status: {
          value: purpose,
          label: "Terminabsage",
          icon: mdiCalendarRemoveOutline,
          color: "rose",
        },
        tooltip:
          "Nachricht, dass ein konkreter Termin abgesagt wurde. Der betroffene Termin steht in der Termin-Spalte.",
      }
    case "APPOINTMENT_CANCEL_REPLAN_LATER":
      return {
        status: {
          value: purpose,
          label: "Termin fällt aus",
          icon: mdiCalendarAlertOutline,
          color: "amber",
        },
        tooltip: "Der bisherige Termin entfällt. Es wurde noch kein neuer Termin kommuniziert.",
      }
    case "APPOINTMENT_RESCHEDULE":
      return {
        status: {
          value: purpose,
          label: "Terminverschiebung",
          icon: mdiCalendarSyncOutline,
          color: "blue",
        },
        tooltip:
          "Nachricht zu einer Terminverschiebung. Die Termin-Spalte zeigt alten und neuen Termin.",
      }
    default:
      return {
        status: {
          value: purpose || "UNKNOWN",
          label: "Nachricht",
          icon: mdiMessageTextOutline,
          color: "#64748b",
        },
        tooltip: "Dokumentierte Nachricht zum Termin oder Auftrag.",
      }
  }
}

export const communicationPurposeLabel = (purpose: string) =>
  communicationPurposePresentation(purpose).status.label

export const communicationChannelLabel = (channel?: string | null) => {
  switch (String(channel || "").toUpperCase()) {
    case "EMAIL":
      return "E-Mail"
    case "MOBILE_MESSAGE":
      return "Mobile Nachricht"
    case "LETTER":
      return "Brief"
    case "PHONE":
      return "Telefon"
    case "CHAT":
      return "Chat"
    case "VOICE":
      return "Telefonbot"
    case "PORTAL":
      return "Terminportal"
    case "INTERNAL":
      return "Intern"
    default:
      return channel ? "Kontaktweg" : null
  }
}

export const communicationStatusDefinition = (status: string): StatusDefinition => {
  switch (status) {
    case "PLANNED":
      return { value: status, label: "Geplant", color: "blue" }
    case "QUEUED":
      return { value: status, label: "Wird gesendet", color: "blue" }
    case "ACCEPTED":
      return { value: status, label: "An Anbieter übergeben", color: "blue" }
    case "SENT":
      return { value: status, label: "Gesendet", color: "emerald" }
    case "FAILED":
      return { value: status, label: "Fehlgeschlagen", color: "rose" }
    case "SKIPPED":
      return { value: status, label: "Nicht gesendet", color: "neutral" }
    case "SUPPRESSED":
      return { value: status, label: "Unterdrückt", color: "neutral" }
    default:
      return { value: status || "UNKNOWN", label: "Status unbekannt", color: "blue" }
  }
}

export const communicationStatusClass = (status: string) => {
  if (status === "FAILED") return "error"

  if (status === "SENT") return "success"

  if (status === "SKIPPED" || status === "SUPPRESSED") return "muted"

  return "pending"
}

export const communicationReasonLabel = (communication: CaseCommunicationProjection) => {
  const reason =
    communication.failure_reason || communication.skipped_reason || communication.suppression_reason
  switch (reason) {
    case "admin_selected_no_customer_message":
      return "im Dialog ohne separate Nachricht gewählt"
    case "no_safe_email_recipient":
      return "keine eindeutige E-Mail-Adresse"
    case "reschedule_pending":
      return "reine Storno-Nachricht wegen Verschiebung unterdrückt"
    default:
      if (reason && communication.failure_reason) return reason

      return reason ? "nicht näher angegebener Grund" : null
  }
}

export const communicationTableDetail = (communication: CaseCommunicationProjection) => {
  const reason = communicationReason(communication)
  switch (reason) {
    case "no_safe_email_recipient":
      return "Keine automatische Nachricht: keine eindeutige E-Mail-Adresse."
    case "admin_selected_no_customer_message":
      return "Keine Nachricht: im Dialog ohne separate Nachricht gewählt."
    case "reschedule_pending":
      return "Keine separate Absage: Termin wird als Verschiebung behandelt."
    default:
      if (communication.failure_reason) return "Fehler: Nachricht konnte nicht gesendet werden."

      return "Nachricht zum Termin dokumentiert."
  }
}

export const communicationMessageResult = (communication: CaseCommunicationProjection) => {
  const status = normalizeCode(communication.status)
  const reason = communicationReason(communication)

  if (status === "FAILED") return "Versand fehlgeschlagen"

  if (status === "SKIPPED" || status === "SUPPRESSED") {
    return reasonResultLabel(reason) ?? "Nicht gesendet"
  }

  if (status === "SENT") return sentResultLabelForPurpose(communication.purpose)

  if (status === "ACCEPTED") return "An Anbieter übergeben"

  if (status === "PLANNED") return "Versand geplant"

  if (status === "QUEUED") return "Versand läuft"

  return "Nachricht dokumentiert"
}

export const communicationMessageType = (
  communication: CaseCommunicationProjection,
): CommunicationMessagePresentation => {
  const type = communicationPurposePresentation(communication.purpose)
  const resultLabel = communicationMessageResult(communication)
  const reason = communicationReasonLabel(communication)

  return {
    status: type.status,
    resultLabel,
    tooltip: tooltipLines([
      type.tooltip,
      `Ergebnis: ${resultLabel}`,
      communication.subject ? `Betreff: ${communication.subject}` : null,
      reason ? `Grund: ${reason}` : null,
      communication.provider_status ? `Providerstatus: ${communication.provider_status}` : null,
      communication.queued_at
        ? `Geplant: ${formatCommunicationDateTime(communication.queued_at)}`
        : null,
      communication.sent_at
        ? `Gesendet: ${formatCommunicationDateTime(communication.sent_at)}`
        : null,
      communication.failed_at
        ? `Fehlgeschlagen: ${formatCommunicationDateTime(communication.failed_at)}`
        : null,
    ]),
  }
}

export const communicationExecutionStatus = (
  communication: CaseCommunicationProjection,
): CommunicationExecutionPresentation => {
  const status = normalizeCode(communication.status)
  const reason = communicationReasonLabel(communication)
  const deliverySubject = communicationDeliverySubject(communication.channel)

  if (status === "SENT") {
    return {
      status: { value: "SENT", label: "Gesendet", icon: mdiCheckBold, color: "emerald" },
      tooltip: `${deliverySubject} wurde gesendet.`,
    }
  }

  if (status === "ACCEPTED") {
    return {
      status: {
        value: "ACCEPTED",
        label: "An Anbieter übergeben",
        icon: mdiProgressClock,
        color: "blue",
      },
      tooltip:
        "Der Versanddienst hat die Nachricht angenommen. Die Zustellung ist noch nicht bestätigt.",
    }
  }

  if (status === "FAILED") {
    return {
      status: {
        value: "FAILED",
        label: "Fehlgeschlagen",
        icon: mdiAlertCircleOutline,
        color: "rose",
      },
      tooltip: reason
        ? `${deliverySubject} konnte nicht gesendet werden.<br>${escapeHtml(reason)}`
        : `${deliverySubject} konnte nicht gesendet werden.`,
    }
  }

  if (status === "SKIPPED") {
    return {
      status: {
        value: "SKIPPED",
        label: "Nicht gesendet",
        icon: mdiCloseCircleOutline,
        color: "neutral",
      },
      tooltip: "KehrBlatt hat bewusst keinen Versand gestartet.",
    }
  }

  if (status === "SUPPRESSED") {
    return {
      status: {
        value: "SUPPRESSED",
        label: "Ohne Nachricht",
        icon: mdiCloseCircleOutline,
        color: "neutral",
      },
      tooltip: "Der Termin wurde bearbeitet, aber ohne separate Nachricht dokumentiert.",
    }
  }

  if (status === "PLANNED") {
    return {
      status: { value: "PLANNED", label: "Geplant", icon: mdiCalendarClockOutline, color: "blue" },
      tooltip: `${deliverySubject} ist geplant, aber noch nicht in der Versandwarteschlange.`,
    }
  }

  return {
    status: { value: "QUEUED", label: "Wird gesendet", icon: mdiProgressClock, color: "blue" },
    tooltip: `${deliverySubject} wurde eingeplant. Der Versand läuft im Hintergrund.`,
  }
}

const detailForPurpose = (purpose: string, channel: string | null, status: string) => {
  const transport = channel ? `per ${channel}` : ""
  const done = status === "SENT" ? "wurde" : "wird"
  const suffix = transport ? ` ${transport}` : ""
  switch (purpose) {
    case "APPOINTMENT_CONFIRMATION":
      return `Bestätigung zum vereinbarten Termin ${done}${suffix} gesendet.`
    case "APPOINTMENT_CANCELLATION":
      return `Absage zum Termin ${done}${suffix} gesendet.`
    case "APPOINTMENT_RESCHEDULE":
      return `Änderung zum Termin ${done}${suffix} gesendet.`
    case "APPOINTMENT_CANCEL_REPLAN_LATER":
      return `Information zum ausgefallenen Termin ${done}${suffix} gesendet.`
    default:
      return channel
        ? `Nachricht ${done} per ${channel} gesendet.`
        : "Nachricht zum Termin wurde dokumentiert."
  }
}

export const communicationDetailText = (communication: CaseCommunicationProjection) => {
  const reason = communicationReasonLabel(communication)
  const status = String(communication.status || "UNKNOWN").toUpperCase()

  if (status === "FAILED") {
    return reason
      ? `Nachricht konnte nicht gesendet werden: ${reason}.`
      : "Nachricht konnte nicht gesendet werden."
  }

  if (status === "SKIPPED" || status === "SUPPRESSED") {
    return reason ? `Nachricht wurde nicht gesendet: ${reason}.` : "Nachricht wurde nicht gesendet."
  }

  if (status === "ACCEPTED") {
    return "Nachricht wurde an den Anbieter übergeben."
  }

  return detailForPurpose(
    communication.purpose,
    communicationChannelLabel(communication.channel),
    status,
  )
}

export const buildCommunicationTimelineItem = (
  communication: CaseCommunicationProjection,
): CaseCommunicationTimelinePresentationItem => {
  const recipient = communication.recipient_value || communication.recipient_display_name || null
  const status = communication.status || "UNKNOWN"

  return {
    id: communication.id,
    createdAt:
      communication.sent_at ||
      communication.queued_at ||
      communication.failed_at ||
      communication.created_at,
    title: communicationPurposeLabel(communication.purpose),
    detail: communicationDetailText(communication),
    channel: communicationChannelLabel(communication.channel),
    status: communicationStatusDefinition(status).label,
    statusClass: communicationStatusClass(status),
    statusDefinition: communicationStatusDefinition(status),
    recipient,
    subject: communication.subject,
    source: "communication",
  }
}

export const buildCommunicationTimelineItems = (
  communications: CaseCommunicationProjection[],
): CaseCommunicationTimelinePresentationItem[] => {
  return communications.map(buildCommunicationTimelineItem).sort((left, right) => {
    const leftMs = Date.parse(left.createdAt || "")
    const rightMs = Date.parse(right.createdAt || "")

    return (Number.isFinite(rightMs) ? rightMs : 0) - (Number.isFinite(leftMs) ? leftMs : 0)
  })
}
